import apiRoutesV2 from 'src/config/api/routes';
import { RootState, store } from 'src/modules/shared/store';
import { StudyCard } from '../types/cards';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setSessionScore, setSummary } from '../entity/StudyCardSlice';
import { PatchCollection } from '@reduxjs/toolkit/dist/query/core/buildThunks';

export const baseURL = process.env.REACT_APP_API_ROOT_V2 || '';

export type StudyCardsTypes = 'study_question' | 'term' | 'vocabulary';

export type StudyCardIds = {
  trainingSessionId?: string;
  studyCardId?: string;
};

type FetchCardsParams = {
  pageId: string;
  cardType: StudyCardsTypes;
};

export type CreateTrainingParams = {
  pageId: string;
  studyCardTypes: string[];
  studyCardPracticeVersion: string;
  isReverseMode: boolean;
  resetAllCards: boolean;
  onlyUnknownCards: boolean;
};

type GetNextCardParams = {
  trainingSessionId: string;
  lastStudyCard?: string;
};

type SendCardScoreParams = {
  isCorrect?: boolean;
  studyCardId: string;
  trainingSessionId: string;
};

export type SessionScoreType = {
  id: string;
  amountOfSessions: number;
  practiceTime: number;
  amountOfCards: number;
  amountOfIsKnown: number;
  totalAmountOfViews: number;
  totalPracticeTime: string;
  cards: Array<{
    type: string;
    quantity: number;
    amountOfViews: number;
    amountOfKnows: number;
  }>;
};

type Response = {
  id: string;
};

type UploadExecelStudyCardsRequest = {
  pageId: string;
  body: any;
};

export type PostAddStudyCards = {
  pageId: string;
  cardType: StudyCardsTypes;
  question: string;
  answer: string;
  optionalExplanation?: string | null;
};

export type PatchtudyCards = PostAddStudyCards & { studyCardId: string };

export type PostUploadExecelStudyCardsResponse = {
  createdTypes: StudyCardsTypes[];
  createdItems: {
    total: number;
    of: number;
  };
};

export const studyCardApi = createApi({
  reducerPath: 'studyCardApi',
  tagTypes: ['study_question', 'term', 'vocabulary'],
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getTrainingSummary: builder.mutation<any, any>({
      query: (pageId: string) => ({
        url: apiRoutesV2.practiceSummary(pageId),
      }),
      transformResponse: (response: { data: Array<{ type: string; quantity: number }> }) => {
        store.dispatch(setSummary(response.data as Array<{ type: string; quantity: number }>));
        return response;
      },
    }),
    getStudyCards: builder.query<StudyCard[], FetchCardsParams>({
      query: ({ pageId, cardType }: FetchCardsParams) => ({
        url: apiRoutesV2.getCardsByType(pageId, cardType),
      }),
      transformResponse: (response: { data: StudyCard[] }) => {
        return response.data;
      },
      providesTags: (result, error, { cardType }) => [{ type: cardType }],
    }),
    createTrainingSession: builder.mutation<Response, CreateTrainingParams>({
      query: (params: CreateTrainingParams) => ({
        url: apiRoutesV2.createPractice(),
        method: 'POST',
        body: {
          ...params,
        },
      }),
      transformResponse: (response: Response) => response,
    }),
    getSessionScore: builder.mutation<SessionScoreType, StudyCardIds>({
      query: ({ trainingSessionId }: StudyCardIds) => ({
        url: apiRoutesV2.getSessionScore(trainingSessionId as string),
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        store.dispatch(setSessionScore(response));
        return response;
      },
    }),
    sendCardScore: builder.mutation<StudyCard, SendCardScoreParams>({
      query: ({ studyCardId, trainingSessionId, isCorrect }: SendCardScoreParams) => ({
        url: apiRoutesV2.sendCardScore(studyCardId as string, trainingSessionId as string),
        method: 'POST',
        params: {
          isCorrect,
        },
      }),
    }),
    getNextCard: builder.mutation<StudyCard, GetNextCardParams>({
      query: (params: GetNextCardParams) => ({
        url: apiRoutesV2.getNextCard(params?.trainingSessionId),
        params: {
          lastStudyCard: params?.lastStudyCard,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    postUploadExecelStudyCards: builder.mutation<
      PostUploadExecelStudyCardsResponse,
      UploadExecelStudyCardsRequest
    >({
      query: ({ pageId, body }) => ({
        url: apiRoutesV2.postUploadExcelStudyCards(pageId),
        body,
        method: 'POST',
      }),
      async onQueryStarted(args, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          const tagsToInvalidate = data.createdTypes.map((type) => ({
            type,
          }));
          dispatch(studyCardApi.util.invalidateTags(tagsToInvalidate));
        } catch (error) {
          console.error('Failed to upload study cards:', error);
        }
      },
    }),
    postAddStudyCards: builder.mutation<StudyCard, PostAddStudyCards>({
      query: (body) => ({
        url: apiRoutesV2.addStudyCards(),
        body,
        method: 'POST',
      }),
      async onQueryStarted({ pageId, cardType, ...post }, { dispatch, queryFulfilled }) {
        let addResult: PatchCollection | null = null;
        try {
          const { data: newCard } = await queryFulfilled;
          addResult = dispatch(
            studyCardApi.util.updateQueryData('getStudyCards', { pageId, cardType }, (draft) => {
              draft.push(newCard);
            })
          );
        } catch {
          addResult?.undo();
        }
      },
    }),
    patchStudyCards: builder.mutation<StudyCard, PatchtudyCards>({
      query: (body) => ({
        url: apiRoutesV2.patchStudyCards(body.studyCardId),
        body,
        method: 'PATCH',
      }),
      async onQueryStarted(
        { studyCardId, pageId, cardType, ...patch },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          studyCardApi.util.updateQueryData('getStudyCards', { pageId, cardType }, (draft) => {
            const cardToUpdate = draft.find((card) => card.id === studyCardId);
            if (cardToUpdate) {
              Object.assign(cardToUpdate, patch);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteStudyCards: builder.mutation<
      void,
      { studyCardId: string; pageId: string; cardType: StudyCardsTypes }
    >({
      query: ({ studyCardId }) => ({
        url: apiRoutesV2.deleteStudyCards(studyCardId),
        method: 'DELETE',
      }),
      async onQueryStarted({ studyCardId, pageId, cardType }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          studyCardApi.util.updateQueryData('getStudyCards', { pageId, cardType }, (draft) => {
            const index = draft.findIndex((card) => card.id === studyCardId);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    reorderStudyCards: builder.mutation<
      void,
      { studyCardId: string; pageId: string; cardType: StudyCardsTypes; position: number }
    >({
      query: ({ studyCardId, position, pageId }) => ({
        url: apiRoutesV2.reorderStudyCards(pageId),
        method: 'PATCH',
        body: { position, studyCardId },
      }),
      async onQueryStarted(
        { studyCardId, pageId, cardType, position },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          studyCardApi.util.updateQueryData('getStudyCards', { pageId, cardType }, (draft) => {
            const index = draft.findIndex((card) => card.id === studyCardId);
            if (index !== -1) {
              const [movedCard] = draft.splice(index, 1);
              draft.splice(position, 0, movedCard);
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useCreateTrainingSessionMutation,
  useGetSessionScoreMutation,
  useSendCardScoreMutation,
  useGetNextCardMutation,
  useGetTrainingSummaryMutation,
  useGetStudyCardsQuery,
  usePostUploadExecelStudyCardsMutation,
  usePostAddStudyCardsMutation,
  usePatchStudyCardsMutation,
  useDeleteStudyCardsMutation,
  useReorderStudyCardsMutation,
} = studyCardApi;
